import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  listCategoriesApi,
  createCategoryApi,
  updateCategoryApi,
  deleteCategoryApi,
  CategoryItem,
} from "../../features/categories/api/category.api"
import {
  PlusIcon,
  TrashIcon,
  TagIcon,
  CheckCircleIcon,
  PencilSquareIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

type DrawerMode = "create" | "edit"

export const CategoryPage = () => {
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [loading, setLoading] = useState(true)

  const [drawerMode, setDrawerMode] = useState<DrawerMode>("create")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CategoryItem | null>(null)

  const [title, setTitle] = useState("")
  const [codename, setCodename] = useState("")
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    listCategoriesApi().then((res) => {
      setCategories(res.data.items)
      setLoading(false)
    })
  }, [])

  const openCreate = () => {
    setDrawerMode("create")
    setTitle("")
    setCodename("")
    setConfirmDelete(false)
    setEditTarget(null)
    setDrawerOpen(true)
  }

  const openEdit = (c: CategoryItem) => {
    setDrawerMode("edit")
    setEditTarget(c)
    setTitle(c.title)
    setCodename(c.codename)
    setConfirmDelete(false)
    setDrawerOpen(true)
  }

  const closeDrawer = () => setDrawerOpen(false)

  const handleCreate = async () => {
    setSaving(true)
    try {
      const res = await createCategoryApi({ title, codename })
      setCategories((prev) => [...prev, { id: res.data.id, title, codename }])
      closeDrawer()
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async () => {
    if (!editTarget) return
    setSaving(true)
    try {
      const res = await updateCategoryApi(editTarget.id, { title, codename })
      setCategories((prev) => prev.map((c) => (c.id === editTarget.id ? res.data : c)))
      closeDrawer()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!editTarget) return
    setDeleting(true)
    try {
      await deleteCategoryApi(editTarget.id)
      setCategories((prev) => prev.filter((c) => c.id !== editTarget.id))
      closeDrawer()
    } finally {
      setDeleting(false)
    }
  }

  const isCreate = drawerMode === "create"
  const canSave = !!title.trim() && !!codename.trim()

  return (
    <DashboardLayout title="Категории">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50/60">
          <div className="flex items-center gap-2">
            <TagIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Все категории</span>
            {!loading && (
              <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full">
                {categories.length}
              </span>
            )}
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Добавить категорию
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Название</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Код</th>
                <th className="px-5 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-40" /></td>
                    <td className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                    <td className="px-5 py-4" />
                  </tr>
                ))
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-5 py-16 text-center">
                    <TagIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Категорий пока нет</p>
                  </td>
                </tr>
              ) : (
                categories.map((c, idx) => (
                  <tr
                    key={c.id}
                    onClick={() => openEdit(c)}
                    className="hover:bg-gray-50/60 transition-colors group cursor-pointer"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="w-6 h-6 rounded-md bg-blue-50 text-blue-500 text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="font-medium text-gray-800">{c.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-md">{c.codename}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 group-hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg opacity-0 group-hover:opacity-100">
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                        Редактировать
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={closeDrawer}
          />
          <div
            className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            <>
              {/* Header */}
              <div className={`bg-gradient-to-r ${isCreate ? "from-blue-600 to-blue-500" : "from-amber-500 to-yellow-400"} px-6 pt-6 pb-8 flex-shrink-0 relative`}>
                <button onClick={closeDrawer}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <XMarkIcon className="w-4 h-4 text-white" />
                </button>

                <div className="flex items-center gap-2 mb-3">
                  <TagIcon className="w-5 h-5 text-white/80" />
                  <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                    {isCreate ? "Новая категория" : "Редактировать"}
                  </span>
                </div>

                <p className="text-white font-semibold text-lg pr-10">
                  {isCreate ? "Создание категории" : editTarget?.title}
                </p>

                {!isCreate && editTarget && (
                  <p className="text-white/70 text-sm mt-1 font-mono">{editTarget.codename}</p>
                )}
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                {/* Title */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Название</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="например, Маркетинг"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
                  />
                </div>

                {/* Codename */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Код</label>
                  <input
                    value={codename}
                    onChange={(e) => setCodename(e.target.value)}
                    placeholder="например, marketing"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
                  />
                  <p className="text-xs text-gray-400">Уникальный идентификатор. Только строчные буквы и дефисы.</p>
                </div>

                {/* Delete (edit mode only) */}
                {!isCreate && (
                  <div className="space-y-2 pt-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Удаление</p>
                    {!confirmDelete ? (
                      <button
                        onClick={() => setConfirmDelete(true)}
                        className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Удалить категорию
                      </button>
                    ) : (
                      <div className="border border-red-200 rounded-xl p-4 bg-red-50 space-y-3">
                        <p className="text-sm text-red-700 font-medium">Подтвердите удаление</p>
                        <p className="text-xs text-red-500">Категория «{editTarget?.title}» будет удалена навсегда.</p>
                        <div className="flex gap-2">
                          <button onClick={() => setConfirmDelete(false)}
                            className="flex-1 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors">
                            Отмена
                          </button>
                          <button disabled={deleting} onClick={handleDelete}
                            className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg text-sm font-medium transition-colors">
                            {deleting
                              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              : <TrashIcon className="w-4 h-4" />}
                            Удалить
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
                <button onClick={closeDrawer}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors">
                  Отмена
                </button>
                <button
                  disabled={saving || !canSave}
                  onClick={isCreate ? handleCreate : handleUpdate}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                >
                  {saving
                    ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <CheckCircleIcon className="w-4 h-4" />}
                  {isCreate ? "Создать" : "Сохранить"}
                </button>
              </div>
            </>
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}
